/* Reading an event's date and time off a form, and deciding when a new one
   may be scheduled. The three forms that create events -- the event form,
   the event modal (calendar, events list, trainer's mock interview page)
   and the mock interviews page -- all go through these, and POST /events
   refuses a past schedule on the server too (event.service.js).

   WHY THE PARSING IS FORGIVING. `<input type="date">` and `type="time"`
   hand back "2026-09-29" and "10:30" -- but only where the browser renders
   its own picker. Where it does not, the field is a plain text box and the
   value is whatever was typed: "29/09/2026", "10:30 AM". `new Date()` of
   that is Invalid Date, `.toISOString()` throws "RangeError: Invalid time
   value", and the trainer saw "Submission failed" with no request in the
   Network tab. So the shapes people actually type are read here, and
   anything still unreadable is reported as such instead of thrown. */

// Picking "now" and submitting a moment later is not scheduling in the past.
const GRACE_MINUTES = 5;

export const PAST_SCHEDULE_MESSAGE = 'This time has already passed. Pick a time later than now.';
export const INVALID_SCHEDULE_MESSAGE =
  'Could not read the date or time. Use the pickers, or type the date as DD/MM/YYYY and the time as 10:30 AM.';

const pad2 = (n) => String(n).padStart(2, '0');

const isRealDay = (y, m, d) => {
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
};

/**
 * A form's date as "YYYY-MM-DD", or null when it cannot be read.
 * Accepts the picker's "YYYY-MM-DD" (anything after it, like a time, is
 * ignored), a Date or moment, and a typed day-first "DD/MM/YYYY" with
 * "/", "-" or "." between -- the way dates are written here.
 */
export const normaliseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date || value?._isAMomentObject) {
    const d = value instanceof Date ? value : value.toDate();
    if (Number.isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }
  const text = String(value).trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(text);
  if (m) {
    const [y, mo, d] = [+m[1], +m[2], +m[3]];
    return isRealDay(y, mo, d) ? `${y}-${pad2(mo)}-${pad2(d)}` : null;
  }
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(text);
  if (m) {
    const [d, mo, y] = [+m[1], +m[2], +m[3]];
    return isRealDay(y, mo, d) ? `${y}-${pad2(mo)}-${pad2(d)}` : null;
  }
  return null;
};

/**
 * A form's time as 24-hour "HH:mm", or null when it cannot be read.
 * Accepts "10:30", "10:30:00", "10.30", and 12-hour "10:30 AM" / "2 pm".
 */
export const normaliseTime = (value) => {
  if (!value) return null;
  if (value instanceof Date || value?._isAMomentObject) {
    const d = value instanceof Date ? value : value.toDate();
    if (Number.isNaN(d.getTime())) return null;
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  }
  const m = /^(\d{1,2})(?:[:.](\d{2}))?(?::\d{2})?\s*([ap])?\.?\s*m?\.?$/i.exec(String(value).trim());
  if (!m) return null;
  let h = +m[1];
  const min = m[2] === undefined ? 0 : +m[2];
  const meridiem = m[3] ? m[3].toLowerCase() : null;
  if (meridiem) {
    if (h < 1 || h > 12) return null;
    if (meridiem === 'p' && h !== 12) h += 12;
    if (meridiem === 'a' && h === 12) h = 0;
  } else if (m[2] === undefined) {
    return null; // a bare "10" is not a time
  }
  if (h > 23 || min > 59) return null;
  return `${pad2(h)}:${pad2(min)}`;
};

/**
 * The date and time as the API stores them -- the day's UTC midnight, and
 * the clock time as UTC on 1970-01-01 (every screen reads both back as
 * UTC) -- or null when either cannot be read.
 */
export const toEventDateTime = (date, time) => {
  const day = normaliseDate(date);
  const clock = normaliseTime(time);
  if (!day || !clock) return null;
  return {
    date: `${day}T00:00:00.000Z`,
    time: `1970-01-01T${clock}:00.000Z`,
  };
};

/** Today as <input type="date"> spells it, on this device's clock. */
export const todayInputValue = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/**
 * Is this date + time earlier than now, on this device's wall clock (the
 * clock the trainer typed it against)? False when either part cannot be
 * read, so the "required" / "could not read" messages speak first.
 */
export const isPastSchedule = (date, time) => {
  const day = normaliseDate(date);
  const clock = normaliseTime(time);
  if (!day || !clock) return false;
  const at = new Date(`${day}T${clock}:00`);
  if (Number.isNaN(at.getTime())) return false;
  return at.getTime() < Date.now() - GRACE_MINUTES * 60000;
};
